/**
 * Las piezas del catalogo publico.
 *
 * Esta es la UNICA pantalla que ve un cliente, y por eso no usa los primitivos
 * del ERP: `Card`, `Badge` y `Button` estan calibrados para una herramienta
 * densa que se opera once horas por dia. Aca manda la identidad de la marca, que
 * es negro sobre blanco, el logo en dos lineas con la manzana en la O, y la
 * franja de color arriba. Ver `design-system/tora/pages/catalogo.md`.
 */
import { useState, type ReactNode } from "react";
import { formatear } from "@tora/shared";

import { colorDeIphone } from "@/components/catalogo/colores";
import { srcImagen } from "@/lib/imagenes";
import { cn } from "@/lib/utils";

import { useFotosDeModelo } from "./consultas";
import {
  ahorroDeOferta,
  estadoDeDisponibilidad,
  etiquetaDeOferta,
  fichaDeUsado,
  fotoDeModelo,
  fotoDeUsado,
  linkWhatsapp,
  mensajeDeOferta,
  nombreLegible,
  numeroWhatsapp,
  precioEnEfectivo,
  type GrupoGeneracion,
  type OfertaPublica,
  type ProductoPublico,
  precioEnDolares,
} from "./logica";

// ─────────────────────────────────────────────────────────────────────────────
// La marca
// ─────────────────────────────────────────────────────────────────────────────

/**
 * La franja de arriba. Es el unico lugar donde la marca usa color.
 *
 * Sale del feed: cada posteo lleva en el borde superior una franja que va de un
 * color a otro. Aca se usa UNA sola de esas, no las seis: puestos todos juntos
 * dejan de ser la marca y pasan a ser un arcoiris.
 */
export function FranjaMarca(): JSX.Element {
  return (
    <div
      aria-hidden="true"
      // Los colores corren solos: `franja-fluye` en globals.css.
      className="franja-fluye h-1 w-full shrink-0"
    />
  );
}

/**
 * La linea de color al pie de cada oferta: la misma franja de arriba, con el
 * mismo movimiento. Es lo que separa una oferta de un producto sin sumar otro
 * color a la marca: la oferta queda firmada con la franja del feed. Por debajo
 * y no como borde entero: un contorno que se mueve compite con la foto y el
 * precio.
 */
function FranjaOferta(): JSX.Element {
  return <span aria-hidden="true" className="franja-fluye absolute inset-x-0 bottom-0 h-1" />;
}

/**
 * El logo tal como esta en el feed: dos lineas en mayusculas pesadas, con la
 * manzana mordida en el lugar de la O de MORDIDA.
 *
 * Viene con la pagina (`public/logo-tienda.png`) y NO depende de la base. Antes
 * se dibujaba con letras y un SVG cuando `empresa_config.logo_url` estaba vacio,
 * y en produccion lo estaba: el cliente veia un logo que no era el del feed. Si
 * el local sube otro desde la configuracion, gana ese.
 */
export function Marca({ logoUrl }: { logoUrl?: string | null }): JSX.Element {
  const [roto, setRoto] = useState(false);
  const subido = srcImagen(logoUrl);
  // Si el subido no carga se cae al de la pagina, no al icono de imagen rota.
  const logo = subido && !roto ? subido : "/logo-tienda.png";
  return (
    <img
      src={logo}
      alt="Manzana Mordida"
      onError={() => setRoto(true)}
      className="mx-auto h-[72px] w-auto object-contain md:h-24 lg:h-[104px]"
    />
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Piezas sueltas
// ─────────────────────────────────────────────────────────────────────────────

export function TituloSeccion({
  children,
  bajada,
  contador,
}: {
  children: ReactNode;
  bajada?: string;
  contador?: number;
}): JSX.Element {
  return (
    <div className="mb-3.5 md:mb-5">
      <div className="flex items-baseline justify-between gap-3">
        <h2 className="text-[19px] font-[700] tracking-[-0.025em] md:text-[26px]">{children}</h2>
        {contador !== undefined ? (
          <span className="text-xs tabular-nums text-[#6E6E6E]">{contador}</span>
        ) : null}
      </div>
      {bajada ? <p className="mt-0.5 text-[13px] text-[#5C5C5C] md:text-sm">{bajada}</p> : null}
    </div>
  );
}

/**
 * El hueco de la foto.
 *
 * Es un cuadrado claro con un glifo de camara, no un borde punteado: en una
 * grilla, el punteado grita "esto esta roto" y esto no esta roto, le falta una
 * foto. Cuando entra la foto, el hueco desaparece sin mover nada de lugar.
 */
function HuecoFoto({ chico }: { chico?: boolean }): JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="#C4C4C4"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={chico ? "size-4" : "size-5"}
    >
      <rect x="3" y="6" width="18" height="14" rx="2" />
      <circle cx="12" cy="13" r="3.5" />
      <path d="M8 6l1.5-2h5L16 6" />
    </svg>
  );
}

export function Foto({
  producto,
  enVezDe,
  chico,
  className,
}: {
  producto: ProductoPublico;
  /**
   * Otra foto del mismo equipo, cuando el cliente eligio un color.
   *
   * Gana sobre la del producto y cae de nuevo en ella si no carga, asi que el
   * color nunca deja la ficha sin imagen.
   */
  enVezDe?: string | null;
  chico?: boolean;
  className?: string;
}): JSX.Element {
  // Un usado sin foto propia usa la de su modelo en su color (`fotoDeUsado`):
  // antes quedaba el hueco gris aunque la foto existiera.
  const { data: fotos } = useFotosDeModelo();
  //
  // Y un sellado sin foto propia usa la de su modelo (`fotoDeModelo`): la del
  // 18 estaba y la tarjeta quedaba igual con el hueco.
  const propia =
    srcImagen(producto.imagen_url) ??
    srcImagen(fotoDeUsado(fotos ?? [], producto, (c) => colorDeIphone(c)?.etiqueta ?? c)) ??
    srcImagen(fotoDeModelo(fotos ?? [], producto));
  const elegida = srcImagen(enVezDe) ?? propia;
  // Una URL pegada a mano puede morirse en cualquier momento, y el icono de
  // imagen rota del navegador es peor que no tener foto: parece un error de la
  // pagina. Si falla, se vuelve al hueco y nadie se entera.
  // Se recuerda CUAL fallo y no un simple "fallo": sin esto, una foto de color
  // rota dejaria en el hueco tambien a la del color siguiente, que puede estar
  // perfecta, y una que vuelve a la del producto se pelearia con ella para
  // siempre.
  const [rotas, setRotas] = useState<ReadonlySet<string>>(() => new Set());
  const candidata = elegida !== null && rotas.has(elegida) ? propia : elegida;
  const src = candidata !== null && rotas.has(candidata) ? null : candidata;
  const mostrar = src !== null;

  // El hueco es gris claro; con foto la tarjeta es blanca. Las fotos de producto
  // vienen con fondo blanco, y un blanco adentro de un gris se ve como un
  // recuadro pegado encima.
  return (
    <div
      className={cn(
        "flex items-center justify-center rounded-md",
        mostrar ? "bg-white" : "bg-[#F7F7F7]",
        className,
      )}
    >
      {mostrar ? (
        // `key`: al cambiar de foto, la anterior no queda puesta mientras
        // carga la nueva (se veia el 17 comun con «Pro Max» elegido).
        <img
          key={src}
          src={src}
          alt=""
          loading="lazy"
          onError={() => setRotas((r) => new Set(r).add(src))}
          // `contain` y no `cover`: una foto que no es cuadrada (la cargada a
          // mano del 18 Pro Max) se recortaba arriba y abajo. Las fotos vienen
          // con fondo blanco, asi que lo que sobra a los costados no se ve.
          className="size-full rounded-md object-contain"
        />
      ) : (
        <HuecoFoto chico={chico} />
      )}
    </div>
  );
}

export function Precio({
  pesos,
  dolares,
  className,
  claseDolares,
}: {
  pesos: string;
  /** El precio en dolares, debajo y mas chico; solo en lo cotizado en USD. */
  dolares?: string | null;
  className?: string;
  /** Clases para la linea de los dolares (la ficha de la PC los pone al lado). */
  claseDolares?: string;
}): JSX.Element {
  return (
    <span className={cn("font-[800] tabular-nums tracking-[-0.025em]", className)}>
      {pesos}
      {dolares ? (
        <span
          className={cn(
            "block text-[0.75em] font-semibold tracking-normal text-[#6E6E6E]",
            claseDolares,
          )}
        >
          {dolares}
        </span>
      ) : null}
    </span>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Agregar a la consulta
// ─────────────────────────────────────────────────────────────────────────────

/**
 * El boton de mas, y el contador en el que se convierte.
 *
 * Se transforma EN EL MISMO LUGAR donde se toco: el pulgar ya esta ahi, y sumar
 * el segundo cargador no deberia obligar a buscar otro control en otra parte de
 * la pantalla.
 */
export function BotonAgregar({
  cantidad,
  onAgregar,
  onQuitar,
  nombre,
  chico,
  /** Un usado es una unidad sola: se agrega o se saca, no se cuenta. */
  unico,
}: {
  cantidad: number;
  onAgregar: () => void;
  onQuitar: () => void;
  nombre: string;
  chico?: boolean;
  unico?: boolean;
}): JSX.Element {
  // 40 y 44 px, no 32 y 38: abajo de 44 el pulgar falla, y en la grilla de tres
  // columnas de accesorios 44 taparia media foto. 40 es lo que entra ahi.
  const alto = chico ? "size-10" : "size-11";

  if (cantidad === 0) {
    return (
      <button
        type="button"
        onClick={onAgregar}
        aria-label={`Agregar ${nombre} a la consulta`}
        className={cn(
          "flex items-center justify-center rounded-full bg-[#0A0A0A] text-white",
          "transition-[transform,background-color] hover:bg-[#2A2A2A] active:scale-95",
          alto,
        )}
      >
        <Mas className={chico ? "size-[15px]" : "size-[17px]"} />
      </button>
    );
  }

  if (unico) {
    return (
      <button
        type="button"
        onClick={onQuitar}
        aria-label={`Sacar ${nombre} de la consulta`}
        className={cn(
          "flex items-center justify-center rounded-full bg-[#0A0A0A] text-white",
          "transition-[transform,background-color] hover:bg-[#2A2A2A] active:scale-95",
          alto,
        )}
      >
        <Tilde className={chico ? "size-[15px]" : "size-[17px]"} />
      </button>
    );
  }

  // Los − y + miden 28 para que la pastilla entre en una tarjeta de 110 de
  // ancho, pero el `after` les da 44 de zona de toque sin cambiar el dibujo.
  return (
    <div
      className={cn(
        "flex items-center gap-0.5 rounded-full bg-[#0A0A0A] px-[5px] text-white",
        chico ? "h-8" : "h-[38px]",
      )}
    >
      <button
        type="button"
        onClick={onQuitar}
        aria-label={`Sacar uno de ${nombre}`}
        className="relative flex size-7 items-center justify-center after:absolute after:-inset-2 after:content-['']"
      >
        <Menos className="size-[15px]" />
      </button>
      <span className="min-w-[14px] text-center text-sm font-[700] tabular-nums">{cantidad}</span>
      <button
        type="button"
        onClick={onAgregar}
        aria-label={`Agregar uno más de ${nombre}`}
        className="relative flex size-7 items-center justify-center after:absolute after:-inset-2 after:content-['']"
      >
        <Mas className="size-[15px]" />
      </button>
    </div>
  );
}

function Mas({ className }: { className?: string }): JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      aria-hidden="true"
      className={className}
    >
      <path d="M12 5v14M5 12h14" />
    </svg>
  );
}

function Menos({ className }: { className?: string }): JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      aria-hidden="true"
      className={className}
    >
      <path d="M5 12h14" />
    </svg>
  );
}

function Tilde({ className }: { className?: string }): JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={className}
    >
      <path d="M20 6 9 17l-5-5" />
    </svg>
  );
}

export function IconoWhatsapp({ className }: { className?: string }): JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={className}
    >
      <path d="M21 11.5a8.4 8.4 0 0 1-9 8.4 8.5 8.5 0 0 1-3.9-.9L3 21l2-5.1A8.4 8.4 0 0 1 12 3a8.4 8.4 0 0 1 9 8.5z" />
    </svg>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Las dos formas de mostrar un producto
// ─────────────────────────────────────────────────────────────────────────────

/** El cartelito negro arriba de la foto: «USADO · EN STOCK», «A PEDIDO». */
function Cartel({ children }: { children: ReactNode }): JSX.Element {
  return (
    <span className="absolute left-2 top-2 rounded bg-[#0A0A0A] px-1.5 py-1 text-[9px] font-[700] tracking-[0.06em] text-white">
      {children}
    </span>
  );
}

/** Un sellado o un accesorio: se elige por modelo, entra en una grilla. */
export function Tarjeta({
  producto,
  cantidad,
  onAgregar,
  onQuitar,
  chico,
  precio,
  nombre: nombrePropio,
}: {
  producto: ProductoPublico;
  cantidad: number;
  onAgregar: () => void;
  onQuitar: () => void;
  chico?: boolean;
  precio: string;
  /** El nombre en la tienda (0095); sin el, el del sistema en limpio. */
  nombre?: string;
}): JSX.Element {
  const nombre = nombrePropio ?? nombreLegible(producto.nombre);
  // Solo se dice lo que no es la regla: en una grilla de accesorios casi todo
  // esta en el local, y veinte carteles «EN STOCK» no dejan ver los dos que
  // no. Antes no se decia nada, y el Pencil sin stock era igual al AirTag.
  const estado = estadoDeDisponibilidad([producto]);
  return (
    <div>
      <div className="relative">
        <Foto producto={producto} chico={chico} className="aspect-square w-full" />
        {estado !== "EN STOCK" ? <Cartel>{estado}</Cartel> : null}
        <div
          className={cn(
            "absolute",
            chico ? "bottom-[5px] right-[5px]" : "bottom-[7px] right-[7px]",
          )}
        >
          <BotonAgregar
            cantidad={cantidad}
            onAgregar={onAgregar}
            onQuitar={onQuitar}
            nombre={nombre}
            chico={chico}
          />
        </div>
      </div>
      <div
        className={cn(
          "leading-snug",
          chico
            ? "mt-2 text-[11px] text-[#3A3A3A] md:text-[13px]"
            : "mt-2.5 text-[13px] font-medium",
        )}
      >
        {nombre}
      </div>
      <Precio
        pesos={precio}
        dolares={precioEnDolares(producto)}
        className={cn("mt-1 block", chico ? "text-[13px] md:text-[15px]" : "text-base")}
      />
    </div>
  );
}

/**
 * Un usado: se elige por unidad, no por modelo, asi que va en lista.
 *
 * En una grilla no entran la bateria, el color y la garantia sin apretarse, y
 * son justamente los tres datos que deciden la compra.
 */
export function FilaUsado({
  producto,
  enConsulta,
  onAgregar,
  onQuitar,
  precio,
  nombre: nombrePropio,
}: {
  producto: ProductoPublico;
  enConsulta: boolean;
  onAgregar: () => void;
  onQuitar: () => void;
  precio: string;
  /** El nombre en la tienda (0095); sin el, el del sistema en limpio. */
  nombre?: string;
}): JSX.Element {
  const nombre = nombrePropio ?? nombreLegible(producto.nombre);
  // Un modelo de la lista no tiene ficha: no hay un aparato del que medir la
  // bateria. El renglon entonces no se dibuja, en vez de repetir diecinueve
  // veces la misma promesa; eso lo dice la bajada de la seccion, una sola vez.
  const ficha = fichaDeUsado(producto);

  return (
    <div className="flex gap-3.5 border-b border-[#ECECEC] py-4">
      <Foto producto={producto} chico className="size-[76px] shrink-0" />
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline justify-between gap-2.5">
          <span className="text-sm font-semibold tracking-[-0.015em]">{nombre}</span>
          <Precio
            pesos={precio}
            dolares={precioEnDolares(producto)}
            className="whitespace-nowrap text-right text-base"
          />
        </div>
        {ficha.length > 0 ? (
          <p className="mt-1.5 text-xs leading-relaxed text-[#5C5C5C]">{ficha.join(". ")}.</p>
        ) : null}
        <div className="mt-2 flex items-center justify-between gap-2">
          {/* Una unidad que no esta en el local no se ofrece a pedido: el
              cartel dice lo que dice la vista, no lo supone. */}
          <span className="text-[11px] font-[700] tracking-[0.06em] text-[#0A0A0A]">
            {estadoDeDisponibilidad([producto])}
          </span>
          <BotonAgregar
            cantidad={enConsulta ? 1 : 0}
            onAgregar={onAgregar}
            onQuitar={onQuitar}
            nombre={nombre}
            chico
            unico
          />
        </div>
      </div>
    </div>
  );
}

/**
 * Un modelo en la grilla: una tarjeta para el iPhone 17 Pro entero, no una por
 * capacidad.
 *
 * No lleva boton de mas. Con dos o mas memorias no hay nada que agregar hasta
 * que el cliente elija cual, y un boton que agrega "la mas barata" sin decirlo
 * es peor que no tenerlo. Toda la tarjeta abre la ficha.
 */
export function TarjetaModelo({
  grupo,
  precioDesde,
  onAbrir,
  chico,
  filas,
}: {
  grupo: GrupoGeneracion;
  precioDesde: string;
  onAbrir: () => void;
  chico?: boolean;
  /** Las de toda la generacion en el catalogo (`filasDeLaGeneracion`), para el cartel. */
  filas?: readonly ProductoPublico[];
}): JSX.Element {
  // "desde" y el pie aparecen cuando hay algo que elegir: otra variante u otra
  // memoria. Con un solo producto son ruido.
  const capacidades = grupo.variantes.reduce((n, v) => n + v.capacidades.length, 0);
  const varias = grupo.variantes.length > 1 || capacidades > 1;
  const pie =
    grupo.variantes.length > 1
      ? grupo.variantes.map((v) => v.etiqueta).join(" · ")
      : capacidades > 1
        ? `${capacidades} memorias`
        : "";

  return (
    // `self-start`: en la grilla el boton se estiraba al alto de la fila y
    // centraba lo de adentro, y las tarjetas con menos texto bajaban la foto.
    <button type="button" onClick={onAbrir} className="group block w-full self-start text-left">
      {/* Con el mouse no hay pulgar que diga "esto se toca": la foto se acerca
          apenas y el nombre se subraya. En el telefono no cambia nada. */}
      <div className="relative overflow-hidden rounded-md">
        <Foto
          producto={grupo.principal}
          chico={chico}
          className="aspect-square w-full transition-transform duration-300 md:group-hover:scale-[1.03]"
        />
        {/* USADO: en la seccion Usados sobra, pero un usado puesto en una
            seccion propia del home se leia como un sellado mas barato. Y
            siempre donde esta: sin «A PEDIDO» ni «SIN STOCK», un Mac que no
            hay se veia igual que un iPhone que se consigue. */}
        <Cartel>
          {[
            grupo.principal.es_usado ? "USADO" : null,
            estadoDeDisponibilidad(filas ?? grupo.variantes.flatMap((v) => v.todas)),
          ]
            .filter(Boolean)
            .join(" · ")}
        </Cartel>
      </div>
      <div
        className={cn(
          "leading-snug underline-offset-2 md:group-hover:underline",
          chico
            ? "mt-2 text-[11px] text-[#3A3A3A]"
            : "mt-2.5 text-[13px] font-medium md:text-[15px]",
        )}
      >
        {grupo.nombre}
      </div>
      {/* Sin flex: con «desde» al lado, los dolares (que van en su renglon)
          quedaban corridos el ancho de esa palabra y no alineaban con los de
          la tarjeta de al lado. */}
      <div className="mt-1">
        {varias ? (
          <span className="mr-1.5 text-[11px] text-[#6E6E6E] md:text-xs">desde</span>
        ) : null}
        <Precio
          pesos={precioDesde}
          dolares={precioEnDolares(grupo.principal)}
          className={chico ? "text-[13px]" : "text-base md:text-lg"}
        />
      </div>
      {pie ? <div className="mt-0.5 text-[11px] text-[#6E6E6E] md:text-xs">{pie}</div> : null}
    </button>
  );
}

/**
 * Una oferta.
 *
 * NO LLEVA BOTON DE AGREGAR, Y ES A PROPOSITO
 * -------------------------------------------
 * Un combo no existe en el stock: no hay un producto "iPhone 15 con cargador".
 * Meterlo al carrito obligaria a que una linea pueda valer distinto que su
 * producto, que es la forma mas facil de mandar un total equivocado. La tarjeta
 * entera es el link: se toca y se abre WhatsApp preguntando por esa oferta.
 *
 * EL COMBO EN NEGRO Y LA BAJA EN BLANCO
 * -------------------------------------
 * Son dos cosas distintas y tienen que distinguirse sin leer el cartelito. El
 * combo es lo que el local esta empujando, asi que se lleva el negro; la baja
 * es el mismo producto de siempre a otro precio, y con el contorno alcanza.
 */
export function TarjetaOferta({
  oferta,
  whatsapp,
  ancha = false,
  producto = null,
  vistaPrevia = false,
}: {
  oferta: OfertaPublica;
  whatsapp: string | null;
  /** En Tienda online: sin link (tocarla edita), pero el boton se dibuja igual. */
  vistaPrevia?: boolean;
  /**
   * El equipo del que habla, si esta publicado: pone su foto a la izquierda y
   * el texto a la derecha. Un plan canje o un envio gratis no hablan de un
   * equipo y van solo con texto.
   */
  producto?: ProductoPublico | null;
  /**
   * Sola en la fila ocupa todo el ancho, con la letra mas grande: una tarjeta
   * de 248 px sola a la izquierda dejaba la mitad de la fila vacia y parecia
   * que faltaba cargar algo.
   */
  ancha?: boolean;
}): JSX.Element {
  // Negras las que son un trato (el combo, el canje); blancas las demas. Dos
  // tonos y no siete: la marca es negro sobre blanco, y lo que distingue a
  // cada tipo es lo que dice, no el color.
  const oscura = oferta.clase === "COMBO" || oferta.clase === "CANJE";
  const link = linkWhatsapp(numeroWhatsapp(whatsapp), mensajeDeOferta(oferta));
  const ahorro = ahorroDeOferta(oferta);
  const enEfectivo = precioEnEfectivo(oferta);
  const tenue = oscura ? "text-[#A8A8A8]" : "text-[#6E6E6E]";
  const [fotoRota, setFotoRota] = useState(false);

  // LA DISTRIBUCION
  // Arriba lo que es (el tipo y el titulo), abajo lo que cuesta y el boton. El
  // pie va con `mt-auto`: en una fila de ofertas todas quedan del mismo alto y
  // los precios y los botones caen a la misma altura, aunque un titulo tenga
  // tres renglones y otro uno.
  const encabezado = (
    <>
      <span
        className={cn(
          "text-[10px] font-[700] tracking-[0.12em]",
          oscura ? "text-white" : "text-[#5C5C5C]",
        )}
      >
        {etiquetaDeOferta(oferta.clase)}
      </span>
      <span
        className={cn(
          "mt-2 text-[17px] font-semibold leading-tight tracking-[-0.02em]",
          ancha && "md:max-w-[24ch] md:text-[28px]",
        )}
      >
        {oferta.titulo}
      </span>
      {oferta.detalle ? (
        <span className={cn("mt-1.5 text-xs", tenue)}>{oferta.detalle}</span>
      ) : null}
    </>
  );

  const tamanoPrecio = cn("text-[22px]", ancha && "md:text-[36px]");
  const pie = (
    // En la ancha de la PC el bloque va centrado en alto: con `mt-auto` el
    // precio se iba al piso y quedaba un hueco entre el titulo y el precio.
    <div className={cn("mt-auto flex flex-col pt-4", ancha && "md:mt-6 md:pt-0")}>
      {/* EFECTIVO: el porcentaje manda; con equipo, el precio que queda. */}
      {oferta.clase === "EFECTIVO" && oferta.porcentaje ? (
        <>
          <Precio pesos={`${oferta.porcentaje} % OFF`} className={tamanoPrecio} />
          {enEfectivo !== null ? (
            <span className={cn("mt-0.5 text-xs", tenue)}>
              {formatear(enEfectivo, "ARS")} en efectivo · lista{" "}
              {formatear(Number(oferta.precio_ars) || 0, "ARS")}
            </span>
          ) : null}
        </>
      ) : oferta.precio_ars !== null ? (
        <Precio pesos={formatear(Number(oferta.precio_ars) || 0, "ARS")} className={tamanoPrecio} />
      ) : null}

      {/* El combo dice cuanto se ahorra; la baja muestra lo que costaba. Es el
          mismo numero contado como se entiende en cada caso. */}
      {/* Contra que se ahorra: «Ahorrás $ 588.300» solo se calculaba contra un
          precio que no aparecia en ningun lado. Es lo que sale todo por
          separado, y se muestra tachado. Si el combo cuesta lo mismo que el
          equipo solo, se dice con todas las letras: lo demas va de regalo. */}
      {oferta.clase === "COMBO" && ahorro !== null ? (
        <span className="mt-0.5 text-xs text-[#A8A8A8]">
          <span className="line-through">
            {formatear(Number(oferta.precio_antes_ars) || 0, "ARS")}
          </span>{" "}
          por separado · ahorrás {formatear(ahorro, "ARS")}
        </span>
      ) : null}
      {oferta.clase === "COMBO" &&
      producto &&
      Math.abs((Number(producto.precio_ars) || 0) - (Number(oferta.precio_ars) || 0)) < 1 ? (
        <span className="mt-1 text-xs font-semibold text-white">
          Al precio del equipo solo: lo demás va de regalo.
        </span>
      ) : null}
      {oferta.clase === "BAJA" && oferta.precio_antes_ars !== null ? (
        <span className="mt-0.5 text-xs text-[#6E6E6E] line-through">
          {formatear(Number(oferta.precio_antes_ars) || 0, "ARS")}
        </span>
      ) : null}

      {/* EL BOTON CONSULTAR
          La tarjeta entera abre WhatsApp, pero nada lo decia: se leia como un
          cartel. El boton dice que se toca y a donde lleva. Es un <span> y no
          un <a>: esta adentro del link de la tarjeta, y un link adentro de
          otro no es valido. A lo ancho de su columna, salvo en la ancha de la
          PC, donde un boton de 600 px parece un error. */}
      {link || vistaPrevia ? (
        <span
          className={cn(
            "mt-3.5 inline-flex h-10 w-full items-center justify-center gap-2 rounded-md px-4 text-sm font-semibold",
            ancha && "md:w-auto md:self-start",
            oscura ? "bg-white text-[#0A0A0A]" : "bg-[#0A0A0A] text-white",
          )}
        >
          <IconoWhatsapp className="size-4" />
          {oferta.clase === "CANJE" ? "Cotizá tu usado" : "Consultar"}
        </span>
      ) : null}
    </div>
  );

  // La foto propia de la oferta gana (0110): esta armada para el recuadro
  // vertical y lo llena. Si no, la del equipo, que es cuadrada y va entera.
  const fotoPropia = srcImagen(oferta.imagen_url);
  const srcFoto = fotoPropia ?? (producto ? srcImagen(producto.imagen_url) : null);
  const conFoto = srcFoto !== null && !fotoRota;

  // LA FOTO
  // Un panel blanco que ocupa el lado izquierdo de borde a borde, con el equipo
  // entero adentro. Se probo recortarla (la mitad de arriba, agrandada) y en
  // el telefono quedaba un costado del equipo y un blanco arriba: las fotos de
  // Apple no tienen todas el equipo en el mismo lugar, y un recorte fijo corta
  // cada una en otro lado.
  const cuerpo = conFoto ? (
    <>
      <div
        className={cn(
          // Un margen del color de la tarjeta alrededor de la foto: sin el, en
          // una oferta negra el blanco de la foto se fundia con la pagina y la
          // tarjeta parecia empezar en la mitad.
          "shrink-0 p-2 pb-3 md:pb-3.5",
          // En la ancha de la PC, un ancho fijo: al 42% de 1200 px la foto era
          // un rectangulo apaisado que cortaba el equipo arriba y abajo.
          ancha ? "w-[42%] md:w-[340px] lg:w-[380px]" : "w-[44%]",
        )}
      >
        <div className="relative size-full overflow-hidden rounded-md bg-white">
          {/* Las fotos traen aire alrededor del equipo: `scale` lo acerca sin
              cortarlo. */}
          <img
            src={srcFoto}
            alt=""
            loading="lazy"
            onError={() => setFotoRota(true)}
            className={cn(
              "absolute inset-0 size-full",
              fotoPropia
                ? "object-cover"
                : cn("scale-[1.3] object-contain", ancha && "md:scale-100 md:p-4"),
            )}
          />
        </div>
      </div>
      <div
        className={cn(
          "flex min-w-0 flex-1 flex-col p-4 pb-5 md:p-5 md:pb-6",
          ancha && "md:justify-center md:p-10",
        )}
      >
        {encabezado}
        {pie}
      </div>
    </>
  ) : (
    <>
      {encabezado}
      {pie}
    </>
  );

  // `relative overflow-hidden` para la linea de color de abajo (`FranjaOferta`).
  const clases = cn(
    "relative flex shrink-0 overflow-hidden rounded-lg transition-colors",
    conFoto ? "min-h-[232px] flex-row md:min-h-[256px]" : "flex-col p-4 pb-5 md:p-5 md:pb-6",
    ancha
      ? cn("w-full", !conFoto && "p-5 md:p-8", conFoto && "md:min-h-[320px]")
      : conFoto
        ? "w-[320px] md:w-[380px]"
        : "w-[248px] md:w-[272px]",
    oscura
      ? "bg-[#0A0A0A] text-white hover:bg-[#2A2A2A]"
      : "border border-[#E0E0E0] text-[#0A0A0A] hover:border-[#0A0A0A]",
  );

  // Sin WhatsApp cargado la tarjeta se muestra igual, pero como texto: un link
  // que no lleva a ningun lado es peor que no tenerlo.
  return link ? (
    <a href={link} target="_blank" rel="noreferrer" className={clases}>
      {cuerpo}
      <FranjaOferta />
    </a>
  ) : (
    <div className={clases}>
      {cuerpo}
      <FranjaOferta />
    </div>
  );
}
